/**
 * Fuente única de verdad de las preguntas y opciones del form nativo de "No Agendamiento".
 *
 * Texto y opciones transcriptos LITERAL desde el form real de Tally
 * (`tally.so/embed/nPX6J1`, relevado vía `window.__NEXT_DATA__.props.pageProps.blocks`,
 * sin enviar ningún submit — 2026-09-18). Las claves (`key`) son las que espera el
 * escenario clon de Make ("[No Agendamiento] Nativo — alta vía webhook directo (CLON)",
 * scenarioId 4927262) — ver memoria del proyecto (`no-agendamiento-migracion-nativa.md`).
 *
 * El escenario de Make no rutea nada (a diferencia de derivaciones) — solo hace un upsert
 * en el Data Store `no_agendamiento_submissions`. El único texto que importa matchear
 * exacto es el motivo principal (`reason`), que es lo único que otros escenarios
 * (retención/seguimiento) leen hoy. El resto de las respuestas se empezó a guardar recién
 * con esta migración (antes se perdían) — no tienen consumidores todavía, pero se
 * transcriben igual de exacto por prolijidad y por si en el futuro se usan.
 */

export interface ChoiceOption {
  /** Texto exacto — es lo que se manda a Make, no cambia aunque el label visual sea distinto. */
  value: string;
  /** Texto corto para mostrar en la tarjeta/opción. Si no está, se usa `value` tal cual. */
  label?: string;
  /** Texto de ayuda más chico, debajo del label — para el detalle que no hace falta gritar. */
  helper?: string;
}

/** Grado de sesiones históricas — mismo cálculo ya usado en usePatientsData.ts (onOpenNoSchedule/onOpenRefer). */
export function sessionsGrade(totalSessions: number): 'A' | 'B' | 'C' | 'D' {
  if (totalSessions >= 10) return 'A';
  if (totalSessions >= 5) return 'B';
  if (totalSessions >= 1) return 'C';
  return 'D';
}

export type MainReasonKey = 'alta' | 'no-continua' | 'no-coordinamos' | 'vacaciones';

export const MAIN_REASON_OPTIONS: (ChoiceOption & { key: MainReasonKey })[] = [
  {
    key: 'alta',
    value: 'Alta consensuada de su proceso terapéutico',
    label: 'Alta consensuada',
    helper: 'Se acordó con el paciente terminar el proceso terapéutico.',
  },
  {
    key: 'no-continua',
    value: 'El paciente decidió no continuar (unilateralmente)',
    label: 'El paciente decidió no continuar',
    helper: 'Fue una decisión unilateral, no charlada en conjunto.',
  },
  {
    key: 'no-coordinamos',
    value: 'No pudimos combinar, pero sigue con su proceso (ej: tiene que ver horarios, quedaba en confirmar luego, no se conectó, etc)',
    label: 'No pudimos coordinar, pero sigue en tratamiento',
    helper: 'Ej: tiene que ver horarios, quedaba en confirmar luego, no se conectó, etc.',
  },
  {
    key: 'vacaciones',
    value: 'Se va de vacaciones, tiene un viaje previsto o una mudanza',
    label: 'Vacaciones, viaje o mudanza',
    helper: 'Tiene una fecha tentativa de vuelta para retomar.',
  },
];

export const ALTA_CONSENSUADA = MAIN_REASON_OPTIONS[0].value;
export const DECIDIO_NO_CONTINUAR = MAIN_REASON_OPTIONS[1].value;
export const NO_PUDIMOS_COMBINAR = MAIN_REASON_OPTIONS[2].value;
export const SE_VA_DE_VACACIONES = MAIN_REASON_OPTIONS[3].value;

export const SUB_REASON_OPTIONS: ChoiceOption[] = [
  {
    value: 'Tiene horarios cambiantes, con lo que no puede comprometerse con un horario fijo.',
    label: 'Horarios cambiantes',
    helper: 'No puede comprometerse con un horario fijo.',
  },
  {
    value: 'Está atravesando una situación personal que dificulta la programación de la sesión.',
    label: 'Situación personal',
    helper: 'Dificulta programar la sesión por ahora.',
  },
  {
    value: 'No puede costear las sesiones por el momento y espera resolver su situación económica para continuar.',
    label: 'Situación económica',
    helper: 'Espera resolverla para poder continuar.',
  },
  {
    value: 'El paciente decidió tomar una pausa en el proceso por el momento, sin más detalles.',
    label: 'Prefirió pausar, sin más detalle',
  },
  { value: 'Lo esperé en la sesión pero nunca se conectó.', label: 'No se conectó a la sesión' },
  { value: 'Vacaciones, viaje o mudanza', label: 'Vacaciones, viaje o mudanza' },
];

export const SUB_REASON_VACACIONES = SUB_REASON_OPTIONS[5].value;

/** Mismas 6 opciones para "sub-motivo" y para "duración de las vacaciones" en el form real. */
export const FOLLOW_UP_WINDOW_OPTIONS: ChoiceOption[] = [
  { value: 'En 7 días' },
  { value: 'En 14 días' },
  { value: 'En 21 días' },
  { value: 'En 30 días' },
  { value: 'En 45 días' },
  { value: 'Más de 45 días' },
];

export const PATIENT_EVOLUTION_OPTIONS: ChoiceOption[] = [
  { value: 'Mejora significativa' },
  { value: 'Mejora moderada' },
  { value: 'Sin cambios' },
  { value: 'Retroceso en algunos aspectos' },
  { value: 'Retroceso significativo' },
];

export interface NoAgendamientoFormValues {
  process_id: string;
  patient_email: string;
  psychologist_email: string;
  patient_name: string;
  reason: string;
  sub_reason: string;
  follow_up_in_days: string;
  session_topics_addressed: string;
  patient_evolution: string;
  future_recommendations: string;
}

export function emptyNoAgendamientoFormValues(): NoAgendamientoFormValues {
  return {
    process_id: '',
    patient_email: '',
    psychologist_email: '',
    patient_name: '',
    reason: '',
    sub_reason: '',
    follow_up_in_days: '',
    session_topics_addressed: '',
    patient_evolution: '',
    future_recommendations: '',
  };
}

/**
 * Identifica al paciente/proceso para el que se abre el popup — lo que hoy arman los 5
 * puntos de entrada como query string para `/patients/no-schedule`. `totalSessionsGrade` es
 * opcional: `ReportAbsenceModal` no lo tiene a mano (ver tallyNoSchedule.ts) — sin grado, el
 * popup no bloquea "Alta consensuada" ni muestra el estado "no aplica" (no hay forma de
 * confirmar sesiones <10 ni ==0 sin ese dato).
 */
export interface NoAgendamientoModalParams {
  processId: string;
  patientEmail: string;
  patientName: string;
  psychologistEmail: string;
  totalSessionsGrade?: string;
}

/**
 * Clave de `location.state` usada para pedirle a `/patients` que abra el popup apenas
 * monta — es cómo `/patients/no-schedule` (la ruta vieja, por si quedó linkeada afuera del
 * repo en algún mail/WhatsApp) sigue funcionando sin tener página propia.
 */
export const OPEN_NO_AGENDAMIENTO_STATE_KEY = 'openNoAgendamiento';

export interface OpenNoAgendamientoState {
  [OPEN_NO_AGENDAMIENTO_STATE_KEY]: NoAgendamientoModalParams;
}
